import { useEffect, useRef, useState } from 'react';

import * as yup from 'yup';
import { FormikValues } from 'formik';
import { toast } from 'react-toastify';
import Link from '@tiptap/extension-link';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Color from '@tiptap/extension-color';
import Table from '@tiptap/extension-table';
import StarterKit from '@tiptap/starter-kit';
import Strike from '@tiptap/extension-strike';
import { useTranslation } from 'react-i18next';
import ListItem from '@tiptap/extension-list-item';
import TableRow from '@tiptap/extension-table-row';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Underline from '@tiptap/extension-underline';
import TableCell from '@tiptap/extension-table-cell';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import { yupResolver } from '@hookform/resolvers/yup';
import BulletList from '@tiptap/extension-bullet-list';
import FontFamily from '@tiptap/extension-font-family';
import Superscript from '@tiptap/extension-superscript';
import OrderedList from '@tiptap/extension-ordered-list';
import TableHeader from '@tiptap/extension-table-header';
import { EditorContent, useEditor } from '@tiptap/react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft } from '@fortawesome/free-regular-svg-icons';
import { Button, ButtonGroup, Card, Col, Form, Row } from 'react-bootstrap';
import { faAlignCenter, faAlignLeft, faAlignRight, faBold, faHighlighter, faItalic, faListOl, faListUl, faRedo, faSave, faStrikethrough, faSubscript, faSuperscript, faTable, faUnderline } from '@fortawesome/free-solid-svg-icons';

import { AESDecrypt } from '../../../encrypt-util';

import styles from './emailTemplateDtls.module.css';
import { getEmailTemplate, updateEmailTemplate } from './emailTemplateActions';
import { updateDashbaordRefresh } from '../../common/components/DataTable/slices/DataTable';
import { AppDispatch } from '../template/store';
import { useBackNavigation } from '../../../hooks/BackNavigation';
import UnsavedChangesModal from '../../../components/common/UnsavedChangesModal';
import { useFormNavigationWarning } from '../../../hooks/useFormNavigationWarning';

const validationSchema = yup.object().shape({
  template_subject: yup.string().required('Template Subject is required'),
  template_body:yup.string().test('is-empty', 'Template Body is required', (value: any) => {
    if (!value) return false;
    // Remove HTML tags and decode HTML entities, then trim
    const textContent = value
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .replace(/&[a-zA-Z0-9#]+;/g, '') // Remove other HTML entities
      .trim();
    
    return textContent.length > 0;
  })
  
});

const MenuBar = ({ editor }: { editor: any }) => {
  if (!editor) {
    return null;
  }

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <ButtonGroup className="mb-2">
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 1 }) ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        H1
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 2 }) ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('bold') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FontAwesomeIcon icon={faBold} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('italic') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FontAwesomeIcon icon={faItalic} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('strike') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <FontAwesomeIcon icon={faStrikethrough} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('underline') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <FontAwesomeIcon icon={faUnderline} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('bulletList') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <FontAwesomeIcon icon={faListUl} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('orderedList') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <FontAwesomeIcon icon={faListOl} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <FontAwesomeIcon icon={faAlignLeft} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <FontAwesomeIcon icon={faAlignCenter} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <FontAwesomeIcon icon={faAlignRight} />
      </Button>
      <Button
        size="sm"
        variant="light"
        onClick={addTable}
      >
        <FontAwesomeIcon icon={faTable} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('highlight') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      >
        <FontAwesomeIcon icon={faHighlighter} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('subscript') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleSubscript().run()}
      >
        <FontAwesomeIcon icon={faSubscript} />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('superscript') ? 'primary' : 'light'}
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
      >
        <FontAwesomeIcon icon={faSuperscript} />
      </Button>
    </ButtonGroup>
  );
};

const EmailTemplateDtls = ({ backPath }: { backPath: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { email_template_id } = useParams();
  const decryptEmailTemplateId = AESDecrypt(email_template_id);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors,isDirty },
    watch,
    reset,
    trigger,
  } = useForm<FormikValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      liner: '',
      country: '',
      template_type: '',
      status: '',
      template_subject: '',
      template_body: '',
      email_default_to: '',
      email_default_cc: '',
      email_default_bcc: '',
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Strike,
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'table table-bordered',
        },
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'table-row',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'table-header',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'table-cell',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      Highlight,
      FontFamily,
      Link,
      Subscript,
      Superscript,
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: '',
    editorProps: {
      attributes: {
        style: 'height: 15rem; overflow: auto;',
        class: `fs--1 form-control ${errors.template_body ? 'is-invalid' : ''}`,
      },
    },
    onUpdate: ({ editor }: any) => {
      setValue('template_body', editor.getHTML().trim());
      trigger('template_body');
    },
  });

  const [isEditorDirty, setIsEditorDirty] = useState(false);
  const initialEditorContent = useRef('');

  useEffect(() => {
    if (editor && watch('template_body')) {
      initialEditorContent.current = watch('template_body');
      editor.commands.setContent(watch('template_body'));
    }
  }, [editor, watch('template_body')]);

  useEffect(() => {
    if (!editor) return;
    const updateDirty = () => {
      const dirty = editor.getHTML() !== initialEditorContent.current;
      setIsEditorDirty(dirty);
    };
    editor.on('update', updateDirty);
    return () => {
      editor.off('update', updateDirty);
    };
  }, [editor]);

  // After save, reset dirty state
  const resetEditorDirty = () => {
    if (editor) initialEditorContent.current = editor.getHTML();
    setIsEditorDirty(false);
  };

  const { showModal, handleNavigation, handleConfirm, handleCancel, handleBack } = useFormNavigationWarning({
    isChanged: isDirty || isEditorDirty,
    isEditable: true,
    onConfirm: () => {
      reset();
      dispatch(updateDashbaordRefresh(true));
      resetEditorDirty();
    }
  });

  const handleStayOnPage = () => {
    handleCancel();
  };

  const onBack = useBackNavigation();

  const handleLeavePage = () => {
      const APP_PATH_PREFIX = process.env.REACT_APP_APP_PATH_PREFIX || '';
    //onBack()
    navigate(`${APP_PATH_PREFIX}/emailTemplateDashboard`)
  };
  useEffect(() => {
    if (editor && watch('template_body')) {
      if (editor.getHTML() !== watch('template_body')) {
        editor.commands.setContent(watch('template_body'));
      }
    }
  }, [editor, watch('template_body')]);

  const onSubmit = (data: FormikValues) => {
    const payload = {
      "emailTemplateId": decryptEmailTemplateId,
      "emailTemplateBody": data.template_body,
      "emailTemplateSubject": data.template_subject,
      "smtpDefaultTo": data.email_default_to,
      "smtpDefaultCc": data.email_default_cc,
      "smtpDefaultBcc": data.email_default_bcc,
      "description": data.description
    }
    dispatch(updateEmailTemplate(payload)).unwrap()
      .then((res:any) => {
        const { status } = res as any;
        if (status === 'success') {
          dispatch(updateDashbaordRefresh(true));
          navigate(backPath);
          resetEditorDirty();
        }
      })
  };

  useEffect(() => {
    dispatch(getEmailTemplate({ emailTemplateId: decryptEmailTemplateId }))
      .unwrap()
      .then((res:any) => {
        const { data, status } = res as any;
        if (!data) {
          return;
        }
        if (status === 'success') {
          reset({
            liner: data.mstPartnerId ?? '',
            country: data.country ?? '',
            template_type: data.emailTemplateTp ?? '',
            status: data.emailTemplateStatus ?? '',
            template_subject: data.emailTemplateSubject ?? '',
            template_body: data.emailTemplateBody ?? '',
            email_default_to: data.smtpDefaultTo ?? '',
            email_default_cc: data.smtpDefaultCc ?? '',
            email_default_bcc: data.smtpDefaultBcc ?? '',
            description: data.description ?? '',
          })
        }
        else {
          toast.error('Unable to get email template details');
        }
      })
      .catch((err:any) => {
        console.error('Error: getEmailTemplate in emailTemplateDtls.tsx', err);
      })
  }, [decryptEmailTemplateId])

  return (
    <Card style={{ height: '100%', overflow: 'auto' }}>
       <UnsavedChangesModal
              show={showModal}
              onHide={handleStayOnPage}
              onConfirm={handleLeavePage}
            />
      <Card.Header style={{position: 'sticky',top: 0,zIndex: 1000,backgroundColor: 'white'}} className='border-bottom d-flex justify-content-between align-items-center'>
        <FontAwesomeIcon
          icon={faArrowAltCircleLeft}
          className='text-warning cursor-pointer fa-lg mb-1'
          onClick={() => navigate(backPath)}
        />
        <div className='row col-12'>
          <h2 className='fs-0 text-primary'>
            {t('resource_3:email_template_Details')}
          </h2>
        </div>
      </Card.Header>
      <Card.Body>
        <div className='container overflow-auto'>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t('resource_1:country')}</Form.Label>
                  <Form.Control
                    size='sm'
                    className={`fs--1 form-control ${errors.contactNo ? 'is-invalid' : ''
                      }`}
                    {...register('country')}
                    type="text"
                    isInvalid={!!errors.country}
                    disabled
                  />
                  {errors.country && (
                    <Form.Control.Feedback type="invalid">
                      {errors.country.message as string}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t('resource_3:email_template_tp')}</Form.Label>
                  <Form.Control
                    {...register('template_type')}
                    type="text"
                    isInvalid={!!errors.template_type}
                    size='sm'
                    className={`fs--1 form-control ${errors.contactNo ? 'is-invalid' : ''
                      }`}
                    disabled
                  />
                  {errors.template_type && (
                    <Form.Control.Feedback type="invalid">
                      {errors.template_type.message as string}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t("resource_3:email_template_status")}</Form.Label>
                  <Form.Control
                    {...register('status')}
                    type="text"
                    size='sm'
                    className={`fs--1 form-control ${errors.contactNo ? 'is-invalid' : ''
                      }`}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">

              <Col md={6}>
                <Form.Group >
                  <Form.Label><span className='text-danger'>*</span>{t("resource_3:email_template_subject")}</Form.Label>
                  <Form.Control
                    {...register('template_subject')}
                    onChange={(e:any) => {
                      setValue('template_subject', e.target.value.trim());
                      trigger('template_subject');
                    }}
                    type="text"
                    size='sm'
                    className={`fs--1 form-control`}
                    isInvalid={!!errors.template_subject}
                  />
                  {errors.template_subject && (
                    <Form.Control.Feedback type="invalid">
                      {errors.template_subject.message as string}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("resource_2:email_description")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    {...register('description')}
                    type="text"
                    size='sm'
                    className={`fs--1 form-control ${errors.contactNo ? 'is-invalid' : ''
                      }`}
                    isInvalid={!!errors.description}
                  />
                  {errors.description && (
                    <Form.Control.Feedback type="invalid">
                      {errors.description.message as string}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label><span className='text-danger'>*</span>{t("resource_3:email_template_body")}</Form.Label>
              <div className={`border rounded p-2 ${styles.editorWrapper}`}>
                <MenuBar editor={editor} />
                <EditorContent className={styles.editor} editor={editor} />
              </div>
              {errors.template_body && (
                <div className="text-danger small">
                  {errors.template_body.message as string}
                </div>
              )}
            </Form.Group>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t("resource_3:smtp_default_to")}</Form.Label>
                  <Form.Control
                    {...register('email_default_to')}
                    type="email"
                    size='sm'
                    className={`fs--1 form-control ${errors.contactNo ? 'is-invalid' : ''
                      }`}
                    isInvalid={!!errors.email_default_to}
                    autoComplete="off"
                  />
                  {errors.email_default_to && (
                    <Form.Control.Feedback type="invalid">
                      {errors.email_default_to.message as string}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t("resource_3:smtp_default_cc")}</Form.Label>
                  <Form.Control
                    {...register('email_default_cc')}
                    type="email"
                    size='sm'
                    className={`fs--1 form-control ${errors.contactNo ? 'is-invalid' : ''
                      }`}
                    isInvalid={!!errors.email_default_cc}
                    autoComplete="off"
                    />
                  {errors.email_default_cc && (
                    <Form.Control.Feedback type="invalid">
                      {errors.email_default_cc.message as string}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t("resource_3:smtp_default_bcc")}</Form.Label>
                  <Form.Control
                    {...register('email_default_bcc')}
                    type="email"
                    size='sm'
                    className={`fs--1 form-control ${errors.contactNo ? 'is-invalid' : ''
                      }`}
                    isInvalid={!!errors.email_default_bcc}
                    autoComplete="off"
                    />
                  {errors.email_default_bcc && (
                    <Form.Control.Feedback type="invalid">
                      {errors.email_default_bcc.message as string}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 mb-1">
              <Button
                variant="falcon-primary"
                size="sm"
                onClick={handleSubmit(onSubmit)}
                type="submit">
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {t('resource_1:submit')}
              </Button>
              <Button
                variant='falcon-danger'
                size='sm'
                onClick={() => navigate(backPath)}
              >
                <FontAwesomeIcon icon={faRedo} className='me-2' />
                {t('resource_1:cancel')}
              </Button>
            </div>
          </Form>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EmailTemplateDtls;